import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import {
  Badge,
  Box,
  Button,
  Card,
  Center,
  Grid,
  Group,
  Pagination,
  Paper,
  Progress,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { IconChartBar, IconMountain, IconShieldCheck } from "@tabler/icons-react";
import { fetcher } from "../../../../services/fetcher";

const activePlansDashboardEndpoint = "dashboard/active-plans-dashboard/";
const plansExpiringSoonEndpoint = "dashboard/plans-expiring-soon/";
const planPopularityEndpoint = "dashboard/plan-popularity/";
const pageSize = 10;

const asArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const pick = (source, keys, fallback = 0) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
};

const resolveMetricBlock = (source, aliases) => {
  for (const alias of aliases) {
    if (source?.[alias] !== undefined && source?.[alias] !== null) return source[alias];
  }
  return null;
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "0";
  if (typeof value === "number") return value.toLocaleString("en-IN");
  return String(value);
};

const getMetricValue = (block) => {
  if (typeof block === "number" || typeof block === "string") return block;
  return pick(block, ["value", "count", "total", "current"], 0);
};

const getMetricDelta = (block) => {
  if (!block || typeof block !== "object") return "";
  return (
    pick(
      block,
      [
        "delta",
        "pct_change",
        "pct-change",
        "percentage_change",
        "change_percent",
        "change",
      ],
      ""
    ) || ""
  );
};

const parseKpiCards = (payload) => {
  const source =
    payload?.metrics ||
    payload?.kpis ||
    payload?.data?.metrics ||
    payload?.data?.kpis ||
    payload?.overview ||
    payload ||
    {};
  const schema = [
    {
      label: "Mother Plans",
      aliases: ["active_mother_plans", "mother_plans", "mother_plan", "mother"],
    },
    {
      label: "Master Plans",
      aliases: ["active_master_plans", "master_plans", "master_plan", "master"],
    },
    {
      label: "Customized Plans",
      aliases: [
        "active_customized_plans",
        "customized_plans",
        "customize_plans",
        "customized",
        "customize",
      ],
    },
  ];

  return schema.map((item) => {
    const block = resolveMetricBlock(source, item.aliases);
    return {
      label: item.label,
      value: getMetricValue(block),
      delta: getMetricDelta(block),
    };
  });
};

const badgeColorByType = (planType) => {
  const value = String(planType || "").toLowerCase();
  if (value.includes("mother")) return "violet";
  if (value.includes("master")) return "blue";
  if (value.includes("custom")) return "teal";
  return "gray";
};

const daysLeftColor = (daysLeft) => {
  const days = Number(daysLeft);
  if (!Number.isFinite(days)) return "gray";
  if (days <= 7) return "red";
  if (days <= 15) return "orange";
  return "green";
};

const buildReportUrl = (baseEndpoint, scope, planType) => {
  const params = new URLSearchParams({
    region: scope,
    plan_type: planType,
  });
  return `${baseEndpoint}?${params.toString()}`;
};

const PlanReports = () => {
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState("both");
  const [planType, setPlanType] = useState("all");
  const [showAllPopularity, setShowAllPopularity] = useState(false);

  const kpiUrl = useMemo(
    () => buildReportUrl(activePlansDashboardEndpoint, scope, planType),
    [scope, planType]
  );
  const expiringUrl = useMemo(
    () => buildReportUrl(plansExpiringSoonEndpoint, scope, planType),
    [scope, planType]
  );
  const popularityUrl = useMemo(
    () => buildReportUrl(planPopularityEndpoint, scope, planType),
    [scope, planType]
  );

  useEffect(() => {
    setPage(1);
  }, [scope, planType]);

  useEffect(() => {
    setShowAllPopularity(false);
  }, [scope, planType]);

  const {
    data: kpiRaw,
    isLoading: kpiLoading,
    error: kpiError,
  } = useSWR(kpiUrl, fetcher, { revalidateOnFocus: false });
  const {
    data: expiringRaw,
    isLoading: expiringLoading,
    error: expiringError,
  } = useSWR(expiringUrl, fetcher, { revalidateOnFocus: false });
  const {
    data: popularityRaw,
    isLoading: popularityLoading,
    error: popularityError,
  } = useSWR(popularityUrl, fetcher, { revalidateOnFocus: false });

  const kpiCards = useMemo(() => parseKpiCards(kpiRaw), [kpiRaw]);
  const expiringRows = useMemo(() => asArray(expiringRaw), [expiringRaw]);
  const popularityRows = useMemo(
    () =>
      asArray(popularityRaw).sort(
        (a, b) => Number(b?.contribution_value || 0) - Number(a?.contribution_value || 0)
      ),
    [popularityRaw]
  );

  const pagedRows = useMemo(
    () => expiringRows.slice((page - 1) * pageSize, page * pageSize),
    [expiringRows, page]
  );
  const totalPages = Math.max(1, Math.ceil(expiringRows.length / pageSize));
  const maxContribution = useMemo(
    () => Math.max(...popularityRows.map((item) => Number(item?.contribution_value || 0)), 0),
    [popularityRows]
  );

  const hasError = kpiError || expiringError || popularityError;
  const kpiIcons = [IconMountain, IconShieldCheck, IconChartBar];
  const visiblePopularityRows = showAllPopularity
    ? popularityRows
    : popularityRows.slice(0, 5);

  return (
    <Stack gap={10}>
      <Paper
        withBorder
        radius="md"
        p={8}
        style={{
          background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
          borderColor: "#dfe8f1",
          boxShadow: "0 2px 10px rgba(9, 44, 76, 0.06)",
        }}
      >
        <Group justify="space-between" align="center" gap={8} wrap="wrap">
          <Text size="xs" fw={700} c="#6C94B4" tt="uppercase">
            Filters
          </Text>
          <Group align="center" gap={8} wrap="wrap">
            <Select
              size="xs"
              w={140}
              placeholder="Scope"
              value={scope}
              onChange={(value) => setScope(value || "both")}
              data={[
                { label: "Both", value: "both" },
                { label: "Domestic", value: "domestic" },
                { label: "International", value: "international" },
              ]}
              allowDeselect={false}
              aria-label="Scope"
              styles={{
                input: {
                  height: 30,
                  minHeight: 30,
                  fontSize: 12,
                  border: "1px solid #e6ebf2",
                  borderRadius: 6,
                },
              }}
            />
            <SegmentedControl
              size="xs"
              value={planType}
              onChange={setPlanType}
              radius="md"
              data={[
                { label: "All", value: "all" },
                { label: "Mother", value: "mother" },
                { label: "Master", value: "master" },
                { label: "Customized", value: "customized" },
              ]}
              styles={{
                root: {
                  height: 30,
                  minHeight: 30,
                  border: "1px solid #e6ebf2",
                  borderRadius: 6,
                  background: "#ffffff",
                },
                label: { fontSize: 12 },
              }}
            />
          </Group>
        </Group>
      </Paper>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={10}>
        {kpiCards.map((item, index) => {
          const Icon = kpiIcons[index] || IconChartBar;
          return (
            <Card
              key={item.label}
              withBorder
              radius="md"
              p={10}
              h="100%"
              style={{
                background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                borderColor: "#dfe8f1",
                boxShadow: "0 4px 12px rgba(9, 44, 76, 0.08)",
                minHeight: 92,
              }}
            >
              <Group justify="space-between" align="center" wrap="nowrap">
                <Stack gap={2}>
                  <Text size={10} fw={700} c="#6C94B4" tt="uppercase" lh={1.2}>
                    {item.label}
                  </Text>
                  <Text size={28} fw={800} lh={1} c="#092C4C">
                    {formatValue(item.value)}
                  </Text>
                  <Text size={11} c="#8398ab" lh={1.2}>
                    {item.delta ? `Change: ${item.delta}` : "Change: -"}
                  </Text>
                </Stack>
                <ThemeIcon
                  variant="light"
                  color="blue"
                  radius="xl"
                  size={30}
                  style={{ flexShrink: 0 }}
                >
                  <Icon size={16} />
                </ThemeIcon>
              </Group>
            </Card>
          );
        })}
      </SimpleGrid>

      {hasError ? (
        <Text c="red">Failed to load reports data.</Text>
      ) : (
        <Grid gutter={10} align="stretch">
          <Grid.Col span={{ base: 12, xl: 8 }}>
            <Card
              withBorder
              radius="md"
              p={10}
              h="100%"
              style={{
                background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
                borderColor: "#dfe8f1",
                boxShadow: "0 4px 12px rgba(9, 44, 76, 0.07)",
              }}
            >
              <Stack gap={10}>
                <Group justify="space-between">
                  <Text fw={700} c="#092C4C">
                    Plans Expiring Soon
                  </Text>
                  <Badge variant="light" color="blue" radius="sm">
                    {expiringRows.length} plans
                  </Badge>
                </Group>

                {expiringLoading ? (
                  <Center py="lg">
                    <Text size="sm" c="dimmed">
                      Loading...
                    </Text>
                  </Center>
                ) : expiringRows.length ? (
                  <>
                    <Box
                      style={{
                        border: "1px solid #e6edf5",
                        borderRadius: 8,
                        overflow: "hidden",
                      }}
                    >
                      <Table stickyHeader highlightOnHover withTableBorder={false}>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th style={{ fontSize: 12, color: "#3f5e7b", fontWeight: 700 }}>Plan Name</Table.Th>
                            <Table.Th style={{ fontSize: 12, color: "#3f5e7b", fontWeight: 700 }}>Plan Type</Table.Th>
                            <Table.Th style={{ fontSize: 12, color: "#3f5e7b", fontWeight: 700 }}>Region</Table.Th>
                            <Table.Th style={{ fontSize: 12, color: "#3f5e7b", fontWeight: 700 }}>
                              Deactivation Date
                            </Table.Th>
                            <Table.Th style={{ fontSize: 12, color: "#3f5e7b", fontWeight: 700 }}>Days Left</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {pagedRows.map((row, index) => (
                            <Table.Tr key={`${row?.id || row?.plan_id || row?.plan_name}-${index}`}>
                              <Table.Td>
                                <Text lineClamp={1} size="sm" fw={500}>
                                  {row?.plan_name || row?.name || "-"}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Badge color={badgeColorByType(row?.plan_type)} variant="light" radius="sm">
                                  {row?.plan_type || "-"}
                                </Badge>
                              </Table.Td>
                              <Table.Td>{row?.region || "-"}</Table.Td>
                              <Table.Td>{row?.deactivation_date || row?.expiry_date || "-"}</Table.Td>
                              <Table.Td>
                                <Text c={daysLeftColor(row?.days_left)} fw={700} size="sm">
                                  {row?.days_left ?? "-"}
                                </Text>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Box>

                    <Group justify="space-between" mt={6}>
                      <Text size="xs" c="dimmed">
                        Showing {pagedRows.length} of {expiringRows.length} records
                      </Text>
                      <Pagination page={page} onChange={setPage} total={totalPages} siblings={1} />
                    </Group>
                  </>
                ) : (
                  <Center py="lg">
                    <Text size="sm" c="dimmed">
                      No expiring plans found.
                    </Text>
                  </Center>
                )}
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, xl: 4 }}>
            <Card
              withBorder
              radius="md"
              p={10}
              h="100%"
              style={{
                background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
                borderColor: "#dfe8f1",
                boxShadow: "0 4px 12px rgba(9, 44, 76, 0.07)",
              }}
            >
              <Stack gap={10}>
                <Group justify="space-between" align="center">
                  <Text fw={700} c="#092C4C">
                    Plan Popularity
                  </Text>
                  <Badge variant="light" color="grape" radius="sm">
                    Top 5
                  </Badge>
                </Group>

                {popularityLoading ? (
                  <Center py="lg">
                    <Text size="sm" c="dimmed">
                      Loading...
                    </Text>
                  </Center>
                ) : popularityRows.length ? (
                  <>
                    {visiblePopularityRows.map((item, index) => {
                      const rank = index + 1;
                      const contributionValue = Number(item?.contribution_value || 0);
                      const progressValue =
                        maxContribution > 0
                          ? Math.min(100, (contributionValue / maxContribution) * 100)
                          : 0;
                      return (
                        <Stack key={`${item?.plan_name || item?.name}-${index}`} gap={6}>
                          <Group justify="space-between" wrap="nowrap">
                            <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                              <Text size="sm" fw={800} c="#4d77a1">
                                {rank}.
                              </Text>
                              <Text size="sm" fw={600} lineClamp={1}>
                                {item?.plan_name || item?.name || "-"}
                              </Text>
                            </Group>
                            <Text size="sm" fw={600} c="#092C4C" style={{ flexShrink: 0 }}>
                              {formatValue(item?.contribution_value)}
                            </Text>
                          </Group>
                          <Progress value={progressValue} size={8} radius="xl" color="blue" />
                        </Stack>
                      );
                    })}
                    {popularityRows.length > 5 ? (
                      <Button
                        variant="subtle"
                        size="xs"
                        mt={0}
                        onClick={() => setShowAllPopularity((prev) => !prev)}
                        style={{ alignSelf: "flex-end" }}
                      >
                        {showAllPopularity ? "View Less" : "View More"}
                      </Button>
                    ) : null}
                  </>
                ) : (
                  <Center py="lg">
                    <Text size="sm" c="dimmed">
                      No popularity data available.
                    </Text>
                  </Center>
                )}
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>
      )}

      {(kpiLoading || (kpiCards.length === 0 && !kpiError)) && (
        <Center py="xs">
          <Text size="sm" c="dimmed">
            Loading reports summary...
          </Text>
        </Center>
      )}
    </Stack>
  );
};

export default PlanReports;